import React, {Fragment, useCallback, useEffect, useState} from "react";
import {ArrowLongLeftIcon, MinusCircleIcon, XCircleIcon} from "@heroicons/react/24/solid";
import ServiceConfiguratorBlockDependencyExpressionGenerator
  from "./ServiceConfiguratorBlockDependencyExpressionGenerator";
import generateExpressionCondition from "../utils/generateExpressionCondition";
import ServiceConfiguratorBlockExpressionHeader from "./ServiceConfiguratorBlockExpressionHeader";
import ServiceConfiguratorBlockCalculationExpressionGenerator
  from "./ServiceConfiguratorBlockCalculationExpressionGenerator";
import ServiceConfiguratorBlockCalculationKCCOptimizationGenerator
  from "./ServiceConfiguratorBlockCalculationKCCOptimizationGenerator";
import ServiceConfiguratorBlockCalculationKColorGenerator from "./ServiceConfiguratorBlockCalculationKColorGenerator";
import ServiceConfiguratorBlockCalculationConnectionGenerator
  from "./ServiceConfiguratorBlockCalculationConnectionGenerator";
import ServiceConfiguratorBlockCalculationSpreadsheetGenerator
  from "./ServiceConfiguratorBlockCalculationSpreadsheetGenerator";
import ServiceConfiguratorBlockCalculationRecursiveSumGenerator
  from "./ServiceConfiguratorBlockCalculationRecursiveSumGenerator";
import {DragDropContext, Draggable, Droppable} from "react-beautiful-dnd";
import {EllipsisVerticalIcon} from "@heroicons/react/24/outline";
import {withTooltip} from "react-tippy";
import {useIntl} from "react-intl";
import Modal from "../ui-components/Modal";

const generalOperators = {
  openP: { show: "(", color: "bg-blue-200", value: "(" },
  closeP: { show: ")", color: "bg-blue-200", value: ")" },
  original_expression: {
    show: "ORIGINAL EXPRESSION",
    color: "bg-gray-100",
    value: "original_expression",
    complex: true,
    not_addable: true,
  }
};

const dependenciesOperators = {
  and: { show: "AND", color: "bg-blue-200", value: "&&" },
  or: { show: "OR", color: "bg-blue-200", value: "||" },
  expression: {
    show: "EXPRESSION",
    color: "bg-green-200",
    value: "expression",
    complex: true,
  },
};

const calculationOperators = {
  multiplication: { show: "*", color: "bg-blue-200", value: "*" },
  division: { show: "/", color: "bg-blue-200", value: "/" },
  subtraction: { show: "-", color: "bg-blue-200", value: "-" },
  addition: { show: "+", color: "bg-blue-200", value: "+" },
  expression: {
    show: "VALUE",
    color: "bg-green-200",
    value: "expression",
    complex: true,
  },
  kcc_optimization: {
    show: "KCC OPTIMIZATION",
    color: "bg-purple-200",
    value: "kcc_optimization",
    complex: true,
  },
  kcolor: {
    show: "K-COLOR",
    color: "bg-purple-200",
    value: "kcolor",
    complex: true,
  },
  connector: {
    show: "CONNECTOR",
    color: "bg-purple-200",
    value: "connector",
    complex: true,
  },
  spreadsheet: {
    show: "SPREADSHEET",
    color: "bg-purple-200",
    value: "spreadsheet",
    complex: true,
  },
  recursive_sum: {
    show: "RECURSIVE SUM",
    color: "bg-green-200",
    value: "recursive_sum",
    complex: true,
  }
};

const operatorsByType = {
  dependencies: {
    ...generalOperators,
    ...dependenciesOperators,
  },
  calculation: {
    ...generalOperators,
    ...calculationOperators,
  },
};

const DisabledOperatorIcon = ({ intl }) =>
  withTooltip(
    () => (
      <MinusCircleIcon className="w-4 mt-0.5 opacity-30 hover:opacity-70" />
    ),
    {
      title: intl.formatMessage({ id: "inactive" }),
      placement: "bottom",
      distance: 20,
      animation: "fade",
      arrow: true,
      hideOnClick: true,
    }
  )();

const ServiceConfiguratorBlockExpressionConfigurator = ({
  setShowDependencies,
  setNewData,
  fullExpression,
  newData,
  questions,
  outputs,
  data,
  type,
  recursiveQuestions,
  showKccOptimizationOutput = true,
  lockMissingSelectedOptions = false,
  service,
  newCrossConfigurationData,
  setNewCrossConfigurationData,
  editable=true,
  fromDesignToQuotation=false,
  ...rest
}) => {
  const intl = useIntl();
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [validation, setValidation] = useState(null);
  const getNestedArrayOfExprsFromOriginalExpression = (originalExpression) => {
    if (!originalExpression) return [];
    const original = fullExpression.find((part) =>
      part.startsWith("{") && JSON.parse(part).original === 'original_expression')
    if (!original) return [];
    const {
      blockId,
      isKcolorProductsDependency,
      KcolorProductAccessKey,
      // same for packages hubspot ticket 37436679835
      isKcolorPackagesDependency,
      KcolorPackageAccessKey
    } = JSON.parse(original);
    // const nestedArrayOfExprs = data.find(d=>d.id === blockId)[type];

    const blockReferenced = data.find(d => d.id === blockId);
    return isKcolorProductsDependency && KcolorProductAccessKey ?
      blockReferenced?.metadata_?.products_dependencies?.[KcolorProductAccessKey]?.[type] || [] :
      /*
      HUBSPOT TICKET 37436679835
      added support for Kcolor packages dependencies inside original expression
      */
      isKcolorPackagesDependency && KcolorProductAccessKey && KcolorPackageAccessKey  ?
        blockReferenced?.metadata_?.products_dependencies?.[KcolorProductAccessKey]?.[KcolorPackageAccessKey]?.[type] || [] :
      blockReferenced[type] || [];
  }
  const updateComplexObject = useCallback(
    (object) => {
      const stringify = JSON.stringify(object);
      setNewData({
        ...newData,
        [type]: newData[type].map((or, index) =>
          index === selectedOperator.index ? stringify : or
        ),
      });
      setSelectedOperator({
        ...selectedOperator,
        value: stringify,
      });
    },
    [setNewData, setSelectedOperator, selectedOperator, newData, type]
  );

  const getShowValue = useCallback((op, defaultValue) => {
    try {
      const object = JSON.parse(op);

      if (object?.outputType) {
        if (object.outputId) {
          return `output ${object.outputId}`;
        }

        return object.value || object.column || defaultValue;
      }
      return defaultValue;
    } catch (error) {
      return defaultValue;
    }
  }, []);

  const isOpActive = (op) => {
    if (type !== "dependencies" || op?.[0] !== "{") return true;

    try {
      const { blockType, inputId, value, operator } = JSON.parse(op);

      if (!["question", "output"].includes(blockType) || !inputId) return true;

      const arr = blockType === "question" ? questions : outputs;
      const target = arr.find((x) => x.id === inputId);
      if (!target?.active) return false;
      // Added check for select and multi_select,
      // if the option is not active we should warn the user as if the whole
      // question is not active HUBSPOT TICKET 16795539597
      if (
        blockType === "question" &&
        ["multi_select", "select"].includes(target?.input_type) &&
        (operator === "===" || operator === "!==")
      ) {
        const inputType = target.input_type;
        const optionId = parseInt(value);
        const option = data.find(b=>b.id === parseInt(value));
        if (!option?.active) {
          return false;
        }
      }

      const getStepById = (sid) => data.find((x) => x.id === sid);

      if (!!target.recursive_question_id) {
        const recursiveQuestion = data.find(
          (x) => x.id === target.recursive_question_id
        );
        if (!recursiveQuestion?.active) return false;
        const step = getStepById(recursiveQuestion.step_id);
        return step?.active;
      } else {
        const step = getStepById(target.step_id);
        return step?.active;
      }
    } catch {
      // if anything went wrong with the parsing, just consider it active
      return true;
    }
  };

  useEffect(() => {

    if (!newData[type].length && validation === null) {
      return;
    }

    if (validation !== null && !newData[type].length) {
      setValidation(null);
      return;
    }

    const createCondition =  (arrayOfExprs) => arrayOfExprs
      .map((v) => {
        if (v[0] === "{") {
          const { operator, inputId, value, defaultValue, original, blockId, isKcolorProductsDependency, KcolorProductAccessKey } = JSON.parse(v);
          const question = questions.find((q) => q.id === inputId);

          let testValue = value;

          if (type === "calculation") {
            if (original === 'recursive_sum') {
              /*
              dummy value, recursive sum must be treated as a number
              is indeed the sum of the values of an output of a recursive question;
              the value is not known at this point so we can't evaluate it and
              we don't need to evaluate it so we can just set it to 0 as a dummy value.
               */
              return 0;
            }
            if (original === 'original_expression') {
              // if (isKcolorProductsDependency && KcolorProductAccessKey) {
              //   const kColorInput = data.find(d => d.id === blockId);
              //   const nestedArrayOfExprs = kColorInput?.metadata_?.products_dependencies?.[KcolorProductAccessKey]?.dependencies || [];
              // } else {
              //   const nestedArrayOfExprs = data.find(d => d.id === blockId)[type];
              // }
              const blockReferenced = data.find(d => d.id === blockId);
              const nestedArrayOfExprs = getNestedArrayOfExprsFromOriginalExpression(v);
              const nestedCondition = generateExpressionCondition(nestedArrayOfExprs);
              try {
                // eslint-disable-next-line
                eval(nestedCondition);
                return true;
              } catch (error) {
                return false;
              }
            }
            if (defaultValue || defaultValue === 0) {
              testValue = defaultValue;
            }
            return testValue;
          }

          return generateExpressionCondition(
            question,
            value, // inputId,
            operator,
            testValue
          );
        }
        return v;
      })
      .join(" ");
    const condition = createCondition(newData[type]);
    try {
      // eslint-disable-next-line
      eval(condition);
      setValidation(true);
    } catch (error) {
      setValidation(false);
    }
  }, [newData, type, validation, questions]);

  const operators = operatorsByType[type];
  const complexOperatorsNames = Object.keys(operators).filter(
    (o) => operators[o].complex === true
  );
  const canSelectAsyncOperator = fullExpression?.length === 0;
  const isAsyncOperatorSelected = (target) =>
    fullExpression.some((part) =>
      part === target
      || (part.startsWith("{") && JSON.parse(part).original === target)
    );

  const isAsyncOperatorSelectedInOriginalExpression = (target) => {
    const original = fullExpression.find((part) =>
      part.startsWith("{") && JSON.parse(part).original === 'original_expression')
    if (!original) return false;
    // const {blockId, isKcolorProductsDependency, KcolorProductAccessKey } = JSON.parse(original);
    // const nestedArrayOfExprs = data.find(d=>d.id === blockId)[type];
    // const blockReferenced = data.find(d => d.id === blockId);
    const nestedArrayOfExprs = getNestedArrayOfExprsFromOriginalExpression(original);

    return nestedArrayOfExprs.some((part) =>
      part === target
      || (part.startsWith("{") && JSON.parse(part).original === target)
    );
  }

  const canSelectRecursiveSum = fullExpression.length === 0;
  const isRecursiveSumSelected = fullExpression.some(
    (part) =>
      part === "recursive_sum" ||
      (part.startsWith("{") && JSON.parse(part).original === "recursive_sum")
  );

  const droppableId = "main-droppable";
  const handleOperatorDrop = (dropped) => {
    // ignore drop outside droppable container
    if (!dropped.destination) return;
    // remove dragged item
    const updatedList = [...fullExpression]; // copy original
    const [reorderedItem] = updatedList.splice(dropped.source.index, 1);
    // add dropped item
    updatedList.splice(dropped.destination.index, 0, reorderedItem);
    // update state
    // setNewData((prev) => ({ ...prev, [type]: updatedList }));
    setNewData({
      ...newData,
      [type]: updatedList,
    });
  };

  return (
    <Fragment>
      {editable && <div className="w-full flex mt-2 mb-4">
        <div
          className="w-10 cursor-pointer h-10"
          onClick={() => {
            setShowDependencies(false);
          }}
        >
          <ArrowLongLeftIcon/>
        </div>
      </div>}
      <ServiceConfiguratorBlockExpressionHeader
        title={type}
        validation={validation}
      />

      <DragDropContext onDragEnd={handleOperatorDrop}>
        <Droppable droppableId={droppableId} direction="horizontal">
          {(provided) => (
            <div
              className="flex items-center overflow-x-auto mt-4 pb-4"
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {fullExpression.map((op, index) => {
                let operator;
                switch (op) {
                  case ")":
                    operator = operators.closeP;
                    break;
                  case "(":
                    operator = operators.openP;
                    break;
                  case "&&":
                    operator = operators.and;
                    break;
                  case "||":
                    operator = operators.or;
                    break;
                  case "*":
                    operator = operators.multiplication;
                    break;
                  case "/":
                    operator = operators.division;
                    break;
                  case "-":
                    operator = operators.subtraction;
                    break;
                  case "+":
                    operator = operators.addition;
                    break;
                  default:
                    const opIdentifier = op.startsWith("{")
                      ? JSON.parse(op).original ?? "expression" // default value for legacy
                      : op;
                    operator = operators[opIdentifier];
                }

                let { color, show } = operator;

                const selected =
                  selectedOperator &&
                  selectedOperator.operator.value === operator.value &&
                  index === selectedOperator.index;

                if (selected) {
                  if (complexOperatorsNames.includes(op) || op?.[0] === "{") {
                    color = "bg-yellow-400";
                  } else {
                    color = "bg-blue-400";
                  }
                }

                return (
                  <Draggable
                    key={index}
                    draggableId={`${droppableId}-${index}`}
                    index={index}
                    isDragDisabled={!editable}
                  >
                    {(provided) => (
                      <div
                        key={index}
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        onClick={() => {
                          if (
                            !selectedOperator ||
                            selectedOperator.index !== index
                          ) {
                            setSelectedOperator({ index, operator, value: op });
                            return;
                          }

                          setSelectedOperator(null);
                        }}
                        className={`relative cursor-pointer m-1 mr-1 pt-2 pb-2 pl-4 pr-4 rounded-lg ml-3 ${color}`}
                      >
                        {
                          index === fullExpression.length - 1 &&
                          editable &&
                          !(operator.value === 'original_expression')  ? (
                          <div
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectedOperator?.index === index) {
                                setSelectedOperator(null);
                              }
                              setNewData({
                                ...newData,
                                [type]: newData[type].slice(0, -1),
                              });
                            }}
                            className="absolute w-6 h-6 -right-3 -bottom-3 text-red-400"
                          >
                            <XCircleIcon />
                          </div>
                        ) : null}
                        {editable && <div
                          className="font-bold bg-gray-200 text-gray-500 py-1 px-0 rounded-xl absolute -left-2 "
                          {...provided.dragHandleProps}
                        >
                          <EllipsisVerticalIcon className="h-3.5"/>
                        </div>}
                        <div className="flex items-center gap-1.5">
                          {!isOpActive(op) && (
                            <DisabledOperatorIcon intl={intl} />
                          )}
                          {getShowValue(op, show)}
                        </div>
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className="flex flex-col w-full mt-4">
        {selectedOperator?.operator?.value === "expression" &&
          (type === "dependencies" ? (
            <ServiceConfiguratorBlockDependencyExpressionGenerator
              updateExpression={updateComplexObject}
              setSelectedOperator={setSelectedOperator}
              expression={selectedOperator.value}
              questions={questions}
              outputs={outputs}
              data={data}
              lockMissingSelectedOptions={lockMissingSelectedOptions}
              editable={editable}
              service={service}
              block={rest.block}
              fromDesignToQuotation={fromDesignToQuotation}
            />
          ) : (
            <ServiceConfiguratorBlockCalculationExpressionGenerator
              updateExpression={updateComplexObject}
              setSelectedOperator={setSelectedOperator}
              expression={selectedOperator.value}
              questions={questions}
              recursiveQuestions={recursiveQuestions}
              outputs={outputs}
              data={data}
              lockMissingSelectedOptions={lockMissingSelectedOptions}
              editable={editable}
              service={service}
              block={rest.block}
            />
          ))}
        {selectedOperator?.operator?.value === "kcc_optimization" &&
          type === "calculation" && (
            <ServiceConfiguratorBlockCalculationKCCOptimizationGenerator
              updateKccOptimization={updateComplexObject}
              kccOptimization={selectedOperator.value}
              setSelectedOperator={setSelectedOperator}
              questions={questions}
              editable={editable}
            />
          )}
        {selectedOperator?.operator?.value === "kcolor" &&
          type === "calculation" && (
            <ServiceConfiguratorBlockCalculationKColorGenerator
              updateKColor={updateComplexObject}
              kColorValue={selectedOperator.value}
              setSelectedOperator={setSelectedOperator}
              questions={questions}
            />
          )}
        {selectedOperator?.operator?.value === "connector" &&
          type === "calculation" && (
            <ServiceConfiguratorBlockCalculationConnectionGenerator
              updateConnection={updateComplexObject}
              connection={selectedOperator.value}
              setSelectedOperator={setSelectedOperator}
              questions={questions}
              data={data}
              editable={editable}
            />
          )}
        {selectedOperator?.operator?.value === "spreadsheet" &&
          type === "calculation" && (
            <ServiceConfiguratorBlockCalculationSpreadsheetGenerator
              updateSpreadsheetConfiguration={updateComplexObject}
              spreadsheetConfiguration={selectedOperator.value}
              setSelectedOperator={setSelectedOperator}
              questions={questions}
              recursiveQuestions={recursiveQuestions}
              editable={editable}
              {...rest}
            />
          )}
        {selectedOperator?.operator?.value === "recursive_sum" &&
          type === "calculation" && (
            <ServiceConfiguratorBlockCalculationRecursiveSumGenerator
              updateRecursiveSumConfiguration={updateComplexObject}
              recursiveSumConfiguration={selectedOperator.value}
              setSelectedOperator={setSelectedOperator}
              questions={questions}
              outputs={outputs}
              recursiveQuestions={recursiveQuestions}
              editable={editable}
              {...rest}
            />
          )}
        {editable && <>
          <div className=" mb-2 flex text-center justify-center">
            <p className="font-bold">ADD OPERATOR</p>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1">
            {Object.keys(operators)
              .filter(
                (key) => (key !== "kcc_optimization" || showKccOptimizationOutput) && !operators[key]?.not_addable
              )
              .map((key) => {
                let {color, show, value} = operators[key];

                const active =
                  selectedOperator && selectedOperator.operator.value === value;
                const disabled =
                  // at least another async operator is selected
                  ["kcc_optimization", "connector", "spreadsheet", "kcolor"].some((target) =>
                    key === target && !canSelectAsyncOperator
                  )
                  || ["kcc_optimization", "connector", "spreadsheet", "kcolor"].some(target =>
                    isAsyncOperatorSelected(target) || isAsyncOperatorSelectedInOriginalExpression(target)
                  )

                if (active) {
                  color = color.split("2").join("4");
                }
                return (
                  <div
                    key={key}
                    onClick={() => {
                      if (isAsyncOperatorSelected("kcc_optimization"))
                        return;

                      if (selectedOperator) {
                        if (active) {
                          setSelectedOperator(null);
                          return;
                        }

                        setNewData({
                          ...newData,
                          [type]: newData[type].map((or, index) =>
                            index === selectedOperator.index ? value : or
                          ),
                        });
                        setSelectedOperator({
                          ...selectedOperator,
                          operator: operators[key],
                          value: operators[key].value,
                        });
                        return;
                      }
                      setNewData({
                        ...newData,
                        [type]: [...newData[type], value],
                      });
                    }}
                    className={`cursor-pointer m-1 pt-2 pb-2 pl-4 pr-4 rounded-lg ${color} ${
                      disabled ? "opacity-50 pointer-events-none" : ""
                    }`}
                  >
                    <p className="no-wrap">{show}</p>
                  </div>
                );
              })}
          </div>
        </>
        }

      </div>

      {selectedOperator?.operator?.value === 'original_expression' && (
        <Modal
          large={true}
          onExit={() => setSelectedOperator(null)}
        >
          <div className={"py-4 px-6"}>
            <ServiceConfiguratorBlockExpressionConfigurator
              setShowDependencies={setShowDependencies}
              // fullExpression={data.find((d) => d.id === rest.block.id)[type]}
              fullExpression={getNestedArrayOfExprsFromOriginalExpression(selectedOperator.value)}
              newData={newData}
              setNewData={setNewData}
              block={rest.block}
              questions={questions}
              recursiveQuestions={recursiveQuestions}
              outputs={data.filter(
                (d) =>
                  d.type === "output" &&
                  ["calculation", "json_output_value"].includes(d.output_type) &&
                  d.id !== rest.block.id
              )}
              data={data}
              type={type}
              service={service}
              editable={false}
              {...rest}
            />
          </div>
        </Modal>
      )}
    </Fragment>
  );
};

export default ServiceConfiguratorBlockExpressionConfigurator;
